import axios from "axios";
import { baseUrl } from "./BaseURL";
import { emitAuthLogout } from "./AuthContext";

/**
 * Axios instance used by every component & hook. Attaches:
 *   - Latest admin JWT from localStorage on every request (fresh read)
 *   - 401/403 interceptor that cleanly dispatches to AuthContext via event
 *   - Timeout so hung requests don't jam the UI indefinitely
 *   - Content-Type only when sending a body (avoids CORS preflights on GETs)
 *
 * Round 2 changes:
 *   - Removed the eager header set at axios.create() time (was stale on login)
 *   - Replaced `window.authLogout` hack with AuthContext event bus
 *   - Added 30s timeout
 *   - Ignores AbortController-cancelled errors cleanly
 */

// withCredentials is INTENTIONALLY false: we authenticate with a Bearer
// JWT in the Authorization header, not session cookies.  Leaving it true
// triggers the strictest CORS path on every preflight (the server can't
// reply with `Access-Control-Allow-Origin: *` and must echo the origin
// verbatim), and gains nothing for a token-based API.
const API = axios.create({
  baseURL: baseUrl,
  withCredentials: false,
  timeout: 30_000,
});

// REQUEST INTERCEPTOR ─────────────────────────────────────────────
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("adminToken");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Only set JSON content-type when sending a body that isn't FormData
  if (config.data && !(config.data instanceof FormData)) {
    config.headers = config.headers || {};
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
  }
  return config;
});

// RESPONSE INTERCEPTOR ─────────────────────────────────────────────
API.interceptors.response.use(
  (response) => response,
  (error) => {
    // Silent ignore for aborted requests (user navigated away mid-fetch)
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }
    const status = error?.response?.status;

    // Maintenance mode — backend's MaintenanceModeFilter returns 503 with
    // {"maintenance":true, "message":"...", "endsAt":"..."}.  Hijack and
    // route the user to /maintenance so every screen doesn't have to
    // handle the error individually.  Skip if we're already there to
    // avoid loops, and skip the polling call itself.
    const url = error?.config?.url || "";
    if (
      status === 503
      && error?.response?.data?.maintenance
      && !url.includes("/api/maintenance")
      && typeof window !== "undefined"
      && window.location.pathname !== "/maintenance"
    ) {
      const payload = error.response.data || {};
      // Use sessionStorage so the new page can read message + return URL
      // without having to push state through navigate (the interceptor
      // doesn't have access to the router).
      try {
        sessionStorage.setItem(
          "prosale:maintenance",
          JSON.stringify({
            message: payload.message || "",
            endsAt:  payload.endsAt  || "",
            from:    window.location.pathname + window.location.search,
          }),
        );
      } catch (_) { /* storage disabled — fine */ }
      window.location.href = "/maintenance";
      return Promise.reject(error);
    }

    // 423 = the module behind this endpoint was switched off in Module
    // Settings (possibly by another admin after this tab loaded its module
    // list). Tell ModuleContext to re-fetch so the sidebar and routes hide the
    // module; the caller still gets the error and its friendly message.
    if (status === 423 && error?.response?.data?.moduleDisabled && typeof window !== "undefined") {
      try {
        window.dispatchEvent(new CustomEvent("prosale:module-disabled", {
          detail: { moduleKey: error.response.data.moduleKey },
        }));
      } catch (_) { /* very old browser — ignore */ }
      return Promise.reject(error);
    }

    // 401 = the session itself is invalid/expired → sign the user out.
    // Uses an event so `AuthContext` stays the single source of truth.
    if (status === 401) {
      emitAuthLogout();
      return Promise.reject(error);
    }

    // 403 = authenticated but NOT authorised for this particular resource.
    // This must NOT log the user out — doing so kicked employees back to the
    // login page whenever they opened a module they lacked one permission for
    // (e.g. Tickets), losing their session for an authorisation problem.
    // Routes are gated by <RequirePermission>, which renders a friendly
    // "you don't have access" page; a stray 403 from a widget or background
    // poll is surfaced to the caller instead of destroying the session.
    return Promise.reject(error);
  }
);

/**
 * Pull the most informative human-readable message out of an axios error.
 * Backend errors flow through GlobalExceptionHandler, which returns
 * `{ success:false, message:"..." }`. Older endpoints may return a plain
 * string body. Falls back to the network/axios message and finally to a
 * generic string so callers can always render *something* in a toast.
 */
export function extractApiError(err, fallback = "Something went wrong") {
  if (!err) return fallback;
  const data = err.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) return data;
    if (typeof data === "object") {
      if (typeof data.message === "string" && data.message.trim()) return data.message;
      if (typeof data.error === "string" && data.error.trim()) return data.error;
    }
  }
  return err.message || fallback;
}

export default API;
